/**
 * Reading a tree's art -- where its trunk is, where an axe lands on it, what its leaves and bark look
 * like -- and cutting the notch an axe leaves in it.
 *
 * Pure pixel work on an RGBA buffer, no pixi and no DOM, so it runs the same in a test as in the game
 * and the numbers below can be checked against the shipped art directly.
 *
 * WHY THE ART IS READ RATHER THAN DESCRIBED. Every tree species is drawn differently: the plants
 * tileset's pines and round oaks have a long straight trunk standing on a soft cast shadow, the
 * blossom tree a thin gnarled one, tree5 a stub buried in a root flare twice its width, tree6's trunk
 * sits a third of a tile right of the cell it is planted in. A per-species table of trunk positions
 * would be six hand-measured entries that silently go stale the first time the Lab redraws a tree.
 * Reading the silhouette gets all six right (see the numbers in tree_art.test.ts) and the next one too.
 *
 * The same read gives the effect its colours, which is the other half of why it is worth doing: the
 * leaves an axe shakes loose are the canopy's own greens -- or the blossom tree's pinks -- and the chips
 * are its own bark.
 */

export interface TreeArt {
    width: number
    height: number
    // The lowest solid row: where the roots meet the ground. The rows under it are the baked cast
    // shadow, which is translucent and is not the tree.
    footY: number
    // The lowest row that is still mostly canopy. Leaves are shaken loose from at or above it.
    canopyBottomY: number
    // Where an axe lands, and the trunk's solid span on that row (inclusive).
    impactY: number
    trunkLeft: number
    trunkRight: number
    // Solid canopy pixels to shake leaves loose from, as flattened x,y pairs: a sample, not all of them.
    canopyPoints: number[]
    // 0xrrggbb, dark to light.
    canopyColors: number[]
    barkColors: number[]
}

// Alpha at or above which a pixel is the tree. The plants tileset bakes each tree's cast shadow into the
// same art at alpha 153; everything that is actually wood or leaf is 255.
const SOLID = 200

// A row is canopy when its solid pixels reach this share of the widest row. 0.6 and not lower: tree5's
// root flare is just over half the width of its crown, and must not be read as the bottom of the canopy.
const CANOPY_SHARE = 0.6

// Where on the trunk an axe lands: about this far above the roots, which is the height of a swing from
// a standing body (the hip line sits ~20px above the soles -- see entity-sort-line-is-the-hip).
const SWING_HEIGHT = 16

// The trunk is searched for in the upper part of the gap between canopy and roots, which keeps a root
// flare -- wider than the trunk above it, and narrowing again to a point at the foot -- out of the
// running for "the narrowest part of the tree".
const TRUNK_BAND = 0.6

const MAX_CANOPY_POINTS = 160

// The tallest stump a felled tree leaves, in px above its roots. The axe bites at hip height, and a stump
// that tall reads as a post standing in the way; the tree is cut through lower, below the notch.
const STUMP_RISE = 9

export const DEFAULT_CANOPY_COLORS = [0x2f5a2a, 0x3f7432, 0x548c3a, 0x6fa046, 0x8bb85a]
export const DEFAULT_BARK_COLORS = [0x3f2a1c, 0x5e3f28, 0x7d5636]

// What the inside of a tree looks like once an axe has opened it: always pale, whatever the bark.
const FRESH_WOOD = 0xe7cb98
const FRESH_WOOD_DEEP = 0xc8a370
const CUT_HEART = 0x785634

const luminance = (rgb: number) => 0.299 * ((rgb >> 16) & 255) + 0.587 * ((rgb >> 8) & 255) + 0.114 * (rgb & 255)

// Colours at a spread of points along the dark-to-light order, de-duplicated. Percentiles rather than a
// histogram's peaks because a canopy is a gradient, and the leaves should span it.
const spread = (colors: number[], at: number[]): number[] => {
    if (!colors.length) return []
    const sorted = colors.slice().sort((a, b) => luminance(a) - luminance(b))
    const out: number[] = []
    for (const p of at) {
        const c = sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]
        if (!out.includes(c)) out.push(c)
    }
    return out
}

const rgbAt = (pixels: ArrayLike<number>, width: number, x: number, y: number) => {
    const i = (y * width + x) * 4
    return (pixels[i] << 16) | (pixels[i + 1] << 8) | pixels[i + 2]
}

// The solid run on row `y` containing column `cx`, or else the one whose middle is nearest it.
const runAt = (solid: Uint8Array, width: number, y: number, cx: number): [number, number] | undefined => {
    let best: [number, number] | undefined
    let bestDistance = Infinity
    let x = 0
    while (x < width) {
        if (!solid[y * width + x]) {
            x++
            continue
        }
        const start = x
        while (x < width && solid[y * width + x]) x++
        const end = x - 1
        const distance = start <= cx && cx <= end ? -1 : Math.abs((start + end) / 2 - cx)
        if (distance < bestDistance) {
            bestDistance = distance
            best = [start, end]
        }
    }
    return best
}

/**
 * Read a tree out of its art. `pixels` is RGBA, row-major, `width` x `height`. Returns undefined for art
 * that is not tree-shaped (no canopy over a trunk), which the caller treats as "use the defaults".
 */
export const analyzeTreeArt = (pixels: ArrayLike<number>, width: number, height: number): TreeArt | undefined => {
    if (!width || !height || pixels.length < width * height * 4) return undefined
    const solid = new Uint8Array(width * height)
    const counts = new Array<number>(height).fill(0)
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if (pixels[(y * width + x) * 4 + 3] >= SOLID) {
                solid[y * width + x] = 1
                counts[y]++
            }
        }
    }

    let footY = -1
    for (let y = height - 1; y >= 0; y--) {
        if (counts[y] >= 2) {
            footY = y
            break
        }
    }
    if (footY < 8) return undefined

    // Where the trunk stands: the median column of the solid pixels just above the roots. Not the art's
    // middle, and not the planted cell's: tree6's trunk is twelve pixels right of both.
    const xs: number[] = []
    for (let y = Math.max(0, footY - 20); y < footY; y++) {
        for (let x = 0; x < width; x++) if (solid[y * width + x]) xs.push(x)
    }
    if (!xs.length) return undefined
    xs.sort((a, b) => a - b)
    const centerX = xs[Math.floor(xs.length / 2)]

    const widest = Math.max(...counts)
    let canopyBottomY = -1
    for (let y = footY - 5; y >= 0; y--) {
        if (counts[y] >= CANOPY_SHARE * widest) {
            canopyBottomY = y
            break
        }
    }
    if (canopyBottomY < 0) return undefined

    // The narrowest rows in the upper part of the trunk, and of those the one nearest swing height.
    const bandTop = canopyBottomY + 2
    const bandBottom = Math.max(bandTop, Math.floor(canopyBottomY + TRUNK_BAND * (footY - canopyBottomY)))
    const runs = new Map<number, [number, number]>()
    let narrowest = Infinity
    for (let y = bandTop; y <= bandBottom && y < footY; y++) {
        const run = runAt(solid, width, y, centerX)
        if (!run) continue
        runs.set(y, run)
        narrowest = Math.min(narrowest, run[1] - run[0] + 1)
    }
    if (!runs.size) return undefined
    let impactY = -1
    runs.forEach((run, y) => {
        if (run[1] - run[0] + 1 > narrowest + 3) return
        if (impactY < 0 || Math.abs(y - (footY - SWING_HEIGHT)) < Math.abs(impactY - (footY - SWING_HEIGHT))) {
            impactY = y
        }
    })
    const [trunkLeft, trunkRight] = runs.get(impactY)!

    // A grid sample of the canopy, thinned evenly to the cap: leaves can come off anywhere on it.
    const grid: number[] = []
    for (let y = 0; y <= canopyBottomY; y += 3) {
        for (let x = 1; x < width; x += 3) {
            if (solid[y * width + x]) grid.push(x, y)
        }
    }
    const stride = Math.max(1, Math.ceil(grid.length / 2 / MAX_CANOPY_POINTS))
    const canopyPoints: number[] = []
    const leafColors: number[] = []
    for (let i = 0; i < grid.length; i += 2 * stride) {
        canopyPoints.push(grid[i], grid[i + 1])
        leafColors.push(rgbAt(pixels, width, grid[i], grid[i + 1]))
    }

    const barkSample: number[] = []
    for (let y = Math.max(0, impactY - 6); y <= Math.min(footY, impactY + 6); y++) {
        for (let x = trunkLeft; x <= trunkRight; x++) {
            if (solid[y * width + x]) barkSample.push(rgbAt(pixels, width, x, y))
        }
    }

    return {
        width,
        height,
        footY,
        canopyBottomY,
        impactY,
        trunkLeft,
        trunkRight,
        canopyPoints,
        canopyColors: spread(leafColors, [0.2, 0.4, 0.6, 0.8, 0.92]),
        barkColors: spread(barkSample, [0.15, 0.5, 0.85]),
    }
}

/**
 * A tree-shaped stand-in for art that could not be read (a test with no pixels, a texture whose source
 * the canvas cannot reach): a trunk up the middle, a canopy over the top half, default colours. Only the
 * leaves and chips use it -- nothing is ever drawn INTO art read this way.
 */
export const fallbackTreeArt = (width: number, height: number): TreeArt => {
    const footY = Math.max(0, Math.round(height - 12))
    const canopyBottomY = Math.round(height * 0.55)
    const cx = width / 2
    const canopyPoints: number[] = []
    const rx = width * 0.4
    const ry = canopyBottomY * 0.48
    const cy = canopyBottomY - ry
    for (let y = 0; y <= canopyBottomY; y += 6) {
        for (let x = 0; x < width; x += 6) {
            if (((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1) canopyPoints.push(x, y)
        }
    }
    const impactY = Math.max(canopyBottomY + 2, footY - SWING_HEIGHT)
    return {
        width,
        height,
        footY,
        canopyBottomY,
        impactY,
        trunkLeft: Math.round(cx - 6),
        trunkRight: Math.round(cx + 5),
        canopyPoints,
        canopyColors: DEFAULT_CANOPY_COLORS,
        barkColors: DEFAULT_BARK_COLORS,
    }
}

/**
 * The art with an axe's notch cut into the trunk, `damage` of the way through (0..1), from the side
 * `side` faces (+1 right, -1 left). Returns a new buffer; `pixels` is not touched.
 *
 * WHAT A NOTCH LOOKS LIKE FROM UP HERE. The camera looks down on the world at an angle, so a wedge cut
 * into the side of a trunk shows as three things, and the cut is drawn as exactly those:
 *   - the silhouette is BITTEN: where the wedge took the wood out there is nothing left in front of the
 *     background, so those pixels go clear. This is what reads at a glance, even on a dark trunk.
 *   - the lower face of the cut is flat and faces up, toward the camera, so it shows as a strip of pale
 *     fresh wood along the bottom of the bite -- lighter toward the bark, deeper-coloured toward the
 *     heart.
 *   - the upper face slants down and away and cannot be seen; its far edge, the deepest point of the
 *     wedge, is the dark heart of the cut.
 * The wedge is tall above the cut line and short below it, the shape of a felling notch, and grows in
 * both depth and height as the tree takes damage, so three axe blows read as three distinct stages.
 */
export const notchTreeArt = (
    pixels: ArrayLike<number>,
    width: number,
    height: number,
    art: TreeArt,
    damage: number,
    side: number,
): Uint8ClampedArray => {
    const out = new Uint8ClampedArray(pixels as ArrayLike<number>)
    const d = Math.max(0, Math.min(1, damage))
    if (d <= 0) return out

    const trunkWidth = art.trunkRight - art.trunkLeft + 1
    const centerX = (art.trunkLeft + art.trunkRight) / 2
    const depth = Math.max(2, Math.round(trunkWidth * (0.2 + 0.45 * d)))
    const above = 1 + Math.round(3.5 * d)
    const below = 1 + Math.round(d)
    const heart = art.barkColors.length ? art.barkColors[0] : CUT_HEART

    // Read solidity off the ORIGINAL art: the rows are cut one at a time, and a row must find its trunk
    // edge where the bark is, not where the row above was just cut back to.
    const solidAt = (x: number, y: number) => pixels[(y * width + x) * 4 + 3] >= SOLID
    const paint = (x: number, y: number, rgb: number, alpha = 255) => {
        const i = (y * width + x) * 4
        out[i] = (rgb >> 16) & 255
        out[i + 1] = (rgb >> 8) & 255
        out[i + 2] = rgb & 255
        out[i + 3] = alpha
    }

    for (let dy = -above; dy <= below; dy++) {
        const y = art.impactY + dy
        if (y < 0 || y >= height) continue
        const taper = dy < 0 ? 1 + dy / (above + 0.5) : 1 - dy / (below + 0.5)
        const cut = Math.max(dy === 0 ? 1 : 0, Math.round(depth * taper))
        if (!cut) continue

        // This row's own trunk edge on the struck side: the bark is not a straight column.
        let left = Math.round(centerX)
        let right = left
        if (!solidAt(left, y)) continue
        while (left > 0 && solidAt(left - 1, y)) left--
        while (right < width - 1 && solidAt(right + 1, y)) right++
        const edge = side > 0 ? right : left
        const room = right - left + 1
        const reach = Math.min(cut, room - 1)

        for (let k = 0; k < reach; k++) {
            const x = edge - k * Math.sign(side || 1)
            if (dy < 0) {
                paint(x, y, 0, 0)
            } else {
                paint(x, y, dy === below || k >= reach * 0.6 ? FRESH_WOOD_DEEP : FRESH_WOOD)
            }
        }
        const deepest = edge - reach * Math.sign(side || 1)
        if (deepest >= 0 && deepest < width && solidAt(deepest, y)) {
            paint(deepest, y, dy < 0 ? heart : CUT_HEART)
        }
    }
    return out
}

/** A felled tree's art, cut in two where the axe went through. Each buffer is the full art's size. */
export interface FelledTreeArt {
    // Everything above the cut: what falls.
    crown: Uint8ClampedArray
    // Everything from the cut down, its flat top painted on: what is left standing.
    stump: Uint8ClampedArray
    // The cast shadow baked under the standing tree, when the art has one. It belonged to the tree, and
    // goes as the tree goes; undefined for art drawn without one.
    shadow?: Uint8ClampedArray
    // The first row of the stump.
    cutY: number
    // The point the crown turns on as it goes over: the trunk's far edge at the cut, on the side it falls.
    hingeX: number
}

/**
 * The art cut through at the axe's notch, for a tree falling toward `side` (+1 right, -1 left).
 *
 * The notch goes in on the side the axe came from -- the side away from the fall -- and the tree hinges
 * on the wood left on the far side, which is how a felled tree goes over: the gap opens toward whoever
 * cut it. The cut itself runs at most STUMP_RISE above the roots, so a short stump is left and the
 * notch goes down with the log.
 *
 * The stump's top is the cut seen from up here: a flat face of pale fresh wood, an ellipse because the
 * camera looks down on it at an angle, rimmed with the bark it was cut through and darker at the heart.
 * It sits on top of the stump, in rows the crown covers until it moves, so the face is uncovered by the
 * tree going over rather than showing through it.
 */
export const fellTreeArt = (
    pixels: ArrayLike<number>,
    width: number,
    height: number,
    art: TreeArt,
    side: number,
): FelledTreeArt => {
    const fall = side < 0 ? -1 : 1
    const notched = notchTreeArt(pixels, width, height, art, 1, -fall)
    const cutY = Math.min(art.footY, Math.max(art.impactY + 1, art.footY - STUMP_RISE))
    const crown = new Uint8ClampedArray(width * height * 4)
    const stump = new Uint8ClampedArray(width * height * 4)
    let shadow: Uint8ClampedArray | undefined
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4
            const a = notched[i + 3]
            if (!a) continue
            // Translucent under the canopy is the baked cast shadow, wherever it reaches up to.
            let target = a < SOLID && y > art.canopyBottomY ? undefined : y < cutY ? crown : stump
            if (!target) target = shadow = shadow || new Uint8ClampedArray(width * height * 4)
            target[i] = notched[i]
            target[i + 1] = notched[i + 1]
            target[i + 2] = notched[i + 2]
            target[i + 3] = a
        }
    }

    // The cut face, across the trunk as it stands at the cut line on the ORIGINAL art.
    const solid = new Uint8Array(width * height)
    for (let i = 0; i < width * height; i++) solid[i] = pixels[i * 4 + 3] >= SOLID ? 1 : 0
    const [left, right] = runAt(solid, width, cutY, (art.trunkLeft + art.trunkRight) / 2) || [art.trunkLeft, art.trunkRight]
    const rx = (right - left + 1) / 2
    const ry = Math.max(1, Math.round(rx * 0.45))
    const cx = left + rx
    const cy = cutY - ry
    const rim = art.barkColors.length ? art.barkColors[0] : DEFAULT_BARK_COLORS[0]
    const bark = art.barkColors.length ? art.barkColors[Math.floor(art.barkColors.length / 2)] : DEFAULT_BARK_COLORS[1]
    const paint = (x: number, y: number, rgb: number) => {
        if (x < 0 || x >= width || y < 0 || y >= height) return
        const i = (y * width + x) * 4
        stump[i] = (rgb >> 16) & 255
        stump[i + 1] = (rgb >> 8) & 255
        stump[i + 2] = rgb & 255
        stump[i + 3] = 255
    }
    const inside = (x: number, y: number) => ((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / (ry + 0.5)) ** 2 <= 1
    for (let y = cy - ry; y < cutY; y++) {
        for (let x = left; x <= right; x++) {
            if (!inside(x, y)) continue
            const edge = !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1)
            const r = Math.hypot((x + 0.5 - cx) / rx, (y + 0.5 - cy) / (ry + 0.5))
            if (edge) {
                // The far rim is the bark's dark outline; the near rim, the bark face of the stump.
                paint(x, y, y < cy ? rim : bark)
            } else {
                paint(x, y, r < 0.3 ? CUT_HEART : r < 0.6 ? FRESH_WOOD_DEEP : FRESH_WOOD)
            }
        }
    }

    return {
        crown,
        stump,
        shadow,
        cutY,
        hingeX: fall > 0 ? right + 1 : left,
    }
}

// A repeatable 0..1 value for an integer point.
const hash2 = (x: number, y: number) => {
    let h = (x * 374761393 + y * 668265263) | 0
    h = Math.imul(h ^ (h >>> 13), 1274126177)
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296
}

// Smooth noise at `cell` px: blotches of about a leaf cluster's size, so the crown comes apart in clumps.
const blotch = (x: number, y: number, cell: number) => {
    const gx = x / cell
    const gy = y / cell
    const x0 = Math.floor(gx)
    const y0 = Math.floor(gy)
    const fx = gx - x0
    const fy = gy - y0
    const sx = fx * fx * (3 - 2 * fx)
    const sy = fy * fy * (3 - 2 * fy)
    const top = hash2(x0, y0) + (hash2(x0 + 1, y0) - hash2(x0, y0)) * sx
    const bottom = hash2(x0, y0 + 1) + (hash2(x0 + 1, y0 + 1) - hash2(x0, y0 + 1)) * sx
    return top + (bottom - top) * sy
}

/**
 * When each pixel of a fallen crown goes, 0..1, as it breaks up and clears away: the far end of the crown
 * first and the butt last, in clumps, with a little pixel grain at the edge. The same for every tree of a
 * kind, so a stage can be baked once and shared.
 */
export const crownBreakupOrder = (x: number, y: number, hingeX: number, cutY: number, reach: number) => {
    const distance = Math.hypot(x - hingeX, y - cutY) / Math.max(1, reach)
    return 0.4 * blotch(x, y, 4) + 0.1 * hash2(x, y) + 0.5 * (1 - Math.min(1, distance))
}

/** How far the furthest point of an art `width` wide reaches from a hinge at (hingeX, cutY). */
export const crownReach = (width: number, hingeX: number, cutY: number) => Math.hypot(Math.max(hingeX, width - hingeX), cutY)

/**
 * crownBreakupOrder for every pixel of the art, worked out once so each break-up stage is a cheap pass
 * (the noise is most of the cost, and a stage is baked mid-animation).
 */
export const crownBreakupOrders = (width: number, height: number, hingeX: number, cutY: number): Float32Array => {
    const orders = new Float32Array(width * height)
    const reach = crownReach(width, hingeX, cutY)
    for (let y = 0; y < Math.min(height, cutY); y++) {
        for (let x = 0; x < width; x++) orders[y * width + x] = crownBreakupOrder(x, y, hingeX, cutY, reach)
    }
    return orders
}

/** `crown` with every pixel cleared whose turn (see crownBreakupOrders) has come by `t`. A new buffer. */
export const breakUpCrown = (crown: ArrayLike<number>, orders: Float32Array, t: number): Uint8ClampedArray => {
    const out = new Uint8ClampedArray(crown as ArrayLike<number>)
    for (let i = 0; i < orders.length; i++) {
        if (out[i * 4 + 3] && orders[i] < t) out[i * 4 + 3] = 0
    }
    return out
}
